import React, { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getSessionId } from "@/lib/session";

const INTERESTS = [
  { value: "sponsor", label: "Sponsor Inquiry" },
  { value: "franchise", label: "Franchise Ownership" },
  { value: "player", label: "Player Registration" },
  { value: "academy", label: "Academy Partnership" },
  { value: "media", label: "Media Inquiry" },
  { value: "general", label: "General" },
];

export default function LeadForm({ defaultInterest = "sponsor", title, subtitle, compact = false }) {
  const [form, setForm] = useState({
    name: "",
    organization: "",
    phone: "",
    email: "",
    city: "",
    interest_type: defaultInterest,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, session_id: getSessionId() });
      toast.success("Thanks! Our team will reach out within 24 hours.");
      setForm({ name: "", organization: "", phone: "", email: "", city: "", interest_type: defaultInterest, message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.message || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="lead-form"
      className="bg-pshl-surface border border-white/10 p-6 md:p-8"
    >
      {title && <h3 className="font-display text-2xl text-white mb-1">{title}</h3>}
      {subtitle && <p className="text-sm text-white/65 mb-6">{subtitle}</p>}

      <div className={`grid ${compact ? "grid-cols-1" : "sm:grid-cols-2"} gap-4`}>
        <div>
          <label className="pshl-label">Full Name *</label>
          <input data-testid="lead-name" required name="name" className="pshl-input" value={form.name} onChange={handleChange} placeholder="Your name" />
        </div>
        <div>
          <label className="pshl-label">Organization</label>
          <input data-testid="lead-organization" name="organization" className="pshl-input" value={form.organization} onChange={handleChange} placeholder="Company / Academy / Brand" />
        </div>
        <div>
          <label className="pshl-label">Email *</label>
          <input data-testid="lead-email" required type="email" name="email" className="pshl-input" value={form.email} onChange={handleChange} placeholder="you@brand.com" />
        </div>
        <div>
          <label className="pshl-label">Phone *</label>
          <input data-testid="lead-phone" required name="phone" className="pshl-input" value={form.phone} onChange={handleChange} placeholder="+91 ..." />
        </div>
        <div>
          <label className="pshl-label">City</label>
          <input data-testid="lead-city" name="city" className="pshl-input" value={form.city} onChange={handleChange} placeholder="Your city" />
        </div>
        <div>
          <label className="pshl-label">Interest Type *</label>
          <select data-testid="lead-interest" name="interest_type" required className="pshl-input" value={form.interest_type} onChange={handleChange}>
            {INTERESTS.map((i) => (
              <option key={i.value} value={i.value} className="bg-pshl-surface">{i.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-4">
        <label className="pshl-label">Message</label>
        <textarea data-testid="lead-message" name="message" rows={4} className="pshl-input" value={form.message} onChange={handleChange} placeholder="Tell us about your interest, scope, timelines..." />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="lead-submit"
        className="mt-6 inline-flex items-center gap-2 bg-pshl-electric hover:bg-pshl-electric/90 disabled:opacity-60 text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-3.5 transition-all hover:shadow-[0_0_24px_rgba(0,85,255,0.4)]"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {submitting ? "Submitting..." : "Submit Inquiry"}
      </button>
    </form>
  );
}
